import { useQuery } from '@tanstack/react-query';
import { createColumnHelper } from '@tanstack/react-table';
import { CoinsIcon, PlusIcon, SquareUserRoundIcon, StoreIcon, TruckIcon } from 'lucide-react';
import { useMemo } from 'react';
import { useNavigate } from 'react-router';

import { CostingStrategy, type CarrierAccountDTO } from '@invenco/shipping-interface';

import { Avatar, AvatarFallback } from '@/components/core/avatar';
import { Button } from '@/components/core/button';
import { DialogTrigger } from '@/components/core/dialog';
import {
  DataTable,
  DataTableFilterMenu,
  DataTablePageContent,
  DataTablePagination,
  DataTableRefreshMenu,
  DataTableToolbar,
  useDataTable,
  useDataTableState,
  useRowNavigationCallback,
} from '@/components/data-table';
import { FormDialog } from '@/components/forms/form-dialog';
import { Header, HeaderActions, HeaderIcon, HeaderTitle } from '@/components/header';
import { Page } from '@/components/layout';
import { useGateways } from '@/gateways/GatewayProvider';
import { carrierInitials } from '@/pages/platform/carriers/carrier-initials';
import { fetchAllPages, formatDateTime } from '@/shared/helpers';
import { useGatewayMutation } from '@/shared/hooks/queries';
import { costingStrategyTitle } from '@/shared/title-maps';

import {
  CreateCarrierAccountDialogContent,
  type CreateCarrierAccountInput,
} from './create-carrier-account-dialog-content';

const columnHelper = createColumnHelper<CarrierAccountDTO>();

const columns = [
  columnHelper.accessor('name', { header: 'Name' }),
  columnHelper.accessor('code', { header: 'Code' }),
  columnHelper.accessor('carrierName', {
    header: 'Carrier',
    cell: ({ getValue }) => {
      const carrierName = getValue();
      return (
        <div className="flex items-center gap-2">
          <Avatar size="sm">
            <AvatarFallback>{carrierInitials(carrierName)}</AvatarFallback>
          </Avatar>
          <span>{carrierName}</span>
        </div>
      );
    },
  }),
  columnHelper.accessor('accountName', { header: 'Account owner' }),
  columnHelper.accessor('costingStrategy', {
    header: 'Strategy',
    cell: ({ getValue }) => costingStrategyTitle[getValue()],
  }),
  columnHelper.accessor('updatedAt', {
    header: 'Updated',
    cell: ({ getValue }) => formatDateTime(getValue()),
  }),
];

function matchesMultiselectFilter(
  cellValue: string,
  filter: { operator: 'in' | 'notIn'; value: string[] } | undefined,
): boolean {
  if (!filter) return true;
  const selected = filter.value.includes(cellValue);
  return filter.operator === 'notIn' ? !selected : selected;
}

function matchesSearch(account: CarrierAccountDTO, query: string): boolean {
  return [account.name, account.code, account.carrierName, account.accountName]
    .join(' ')
    .toLowerCase()
    .includes(query);
}

export function CarrierAccountList() {
  const { shippingGateway } = useGateways();
  const navigate = useNavigate();
  const onClickRow = useRowNavigationCallback<CarrierAccountDTO>('/shipping/carrier-accounts');

  const { mutate: createCarrierAccount } = useGatewayMutation<
    CarrierAccountDTO,
    CreateCarrierAccountInput
  >({
    mutationFn: (values) => shippingGateway.createCarrierAccount(values),
    linkedQuery: ['carrierAccounts'],
    linkResponseToQuery: false,
    successMessage: 'Carrier account created',
    onSuccess: (created) => void navigate(`/shipping/carrier-accounts/${created.id}`),
  });

  const { data, isLoading, isFetching, isError, refetch } = useQuery({
    queryKey: ['carrierAccounts'],
    queryFn: ({ signal }) =>
      fetchAllPages(({ cursor, take }) =>
        shippingGateway.getCarrierAccounts({ cursor, take }, { signal }),
      ),
  });

  // The list loads every account up front (fetchAllPages), and the gateway has no free-text or
  // costing-strategy filter, so search and filtering are applied client-side over the loaded rows.
  const carrierOptions = useMemo(
    () =>
      [...new Set((data ?? []).map((account) => account.carrierName))].sort((a, b) =>
        a.localeCompare(b),
      ),
    [data],
  );
  const accountOptions = useMemo(
    () =>
      [...new Set((data ?? []).map((account) => account.accountName))].sort((a, b) =>
        a.localeCompare(b),
      ),
    [data],
  );

  const { state, filters, onPaginationChange, onSortingChange } = useDataTableState({
    columns,
    filterDefs: {
      carrierName: {
        label: 'Carrier',
        variant: 'multiselect',
        icon: TruckIcon,
        options: carrierOptions,
      },
      accountName: {
        label: 'Account owner',
        variant: 'multiselect',
        icon: StoreIcon,
        options: accountOptions,
      },
      costingStrategy: {
        label: 'Strategy',
        variant: 'multiselect',
        icon: CoinsIcon,
        options: Object.values(CostingStrategy),
        optionLabel: (option) => costingStrategyTitle[option as CostingStrategy] ?? option,
      },
    },
  });

  const activeFilters = filters.getActiveFilterMap();
  const { search } = filters;

  const rows = useMemo(() => {
    if (!data) return data;
    const query = search.trim().toLowerCase();
    return data.filter(
      (account) =>
        matchesMultiselectFilter(account.carrierName, activeFilters.carrierName) &&
        matchesMultiselectFilter(account.accountName, activeFilters.accountName) &&
        matchesMultiselectFilter(account.costingStrategy, activeFilters.costingStrategy) &&
        (!query || matchesSearch(account, query)),
    );
  }, [data, activeFilters, search]);

  const table = useDataTable({
    data: rows,
    columns,
    isLoading,
    isLoadingWithStaleData: isFetching && !!data,
    hasError: isError,
    manualPagination: false,
    manualSorting: false,
    autoResetPageIndex: false,
    onPaginationChange,
    onSortingChange,
    state,
  });

  return (
    <Page>
      <Header>
        <HeaderIcon>
          <SquareUserRoundIcon />
        </HeaderIcon>
        <HeaderTitle>Carrier accounts</HeaderTitle>
        <HeaderActions>
          <FormDialog>
            <DialogTrigger asChild>
              <Button>
                <PlusIcon />
                Create
              </Button>
            </DialogTrigger>
            <CreateCarrierAccountDialogContent onSubmit={createCarrierAccount} />
          </FormDialog>
        </HeaderActions>
      </Header>
      <DataTablePageContent>
        <DataTableToolbar>
          <DataTableFilterMenu filters={filters} />
          <DataTableRefreshMenu onRefresh={refetch} />
        </DataTableToolbar>
        <DataTable table={table} onClickRow={onClickRow} />
        <DataTablePagination table={table} />
      </DataTablePageContent>
    </Page>
  );
}
